import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { componentStylesByTheme } from '../utils/theme.utils';
import { THEME_TOKEN, Theme } from '../types/theme.types';
import {
  calendarButtonSAG,
  calendarButtonSHS,
  calendarContainerSAG,
  calendarContainerSHS,
  calendarDayBoxSAG,
  calendarDayBoxSHS,
  calendarDayTextSAG,
  calendarDayTextSHS,
  calendarDisabledDayBoxSAG,
  calendarDisabledDayBoxSHS,
  calendarDividerSAG,
  calendarDividerSHS,
  calendarMonthBoxSAG,
  calendarMonthBoxSHS,
  calendarTopTextSAG,
  calendarTopTextSHS,
} from './calendar-range.styles';

@Component({
  selector: 'sag-ui-calendar-range',
  templateUrl: './calendar-range.component.html',
})
export class CalendarRangeComponent implements OnInit, OnChanges {
  @Input() lang?: 'en' | 'de';
  @Input() startDate?: Date = new Date();
  @Input() endDate?: Date;
  @Input() availableRanges: { startDate: Date; endDate: Date }[] = [];
  @Output() dateRangeChanged: EventEmitter<{
    startDate: Date | null;
    endDate: Date | null;
  }> = new EventEmitter<{ startDate: Date | null; endDate: Date | null }>();

  protected currentMonth: number = new Date().getMonth();
  protected currentYear: number = new Date().getFullYear();
  protected selectedMonth: number = this.startDate?.getMonth() ?? 0;
  protected selectedYear: number = this.startDate?.getFullYear() ?? 0;
  protected selectedDay: number = this.startDate?.getDate() ?? 0;
  protected selectedHours: number = this.startDate?.getHours() ?? 0;
  protected selectedMinutes: number = this.startDate?.getMinutes() ?? 0;
  protected selectedSeconds: number = this.startDate?.getSeconds() ?? 0;
  protected previousMonthsDates: number[] = [];
  protected dates: number[] = [];
  protected nextMonthsDates: number[] = [];
  protected view: 'days' | 'months' | 'years' = 'days';
  protected displayedYears: number[] = Array.from(
    { length: 12 },
    (_, i) => new Date().getFullYear() + i,
  );
  protected Math: Math = Math;

  protected rangeStart: Date | null = null;
  protected rangeEnd: Date | null = null;

  constructor(@Inject(THEME_TOKEN) private readonly theme: Theme) {}

  ngOnInit() {
    this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['startDate'] && this.startDate) {
      this.rangeStart = this.startDate;
      this.selectedMonth = this.startDate.getMonth();
      this.selectedYear = this.startDate.getFullYear();
      this.selectedDay = this.startDate.getDate();
      this.selectedHours = this.startDate.getHours();
      this.selectedMinutes = this.startDate.getMinutes();
      this.selectedSeconds = this.startDate.getSeconds();
      this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
    }

    if (changes['endDate'] && this.endDate) {
      this.rangeEnd = this.endDate;
    }
  }

  protected isDayInRange(day: number): boolean {
    if (!this.rangeStart) return false;

    const currentDate = new Date(this.selectedYear, this.selectedMonth, day);
    if (this.rangeEnd) {
      return currentDate >= this.rangeStart && currentDate <= this.rangeEnd;
    }
    return currentDate.getTime() === this.rangeStart.getTime();
  }

  protected isInAvailableRange(day: number): boolean {
    if (this.availableRanges.length === 0) return true;
    const currentDate = new Date(this.selectedYear, this.selectedMonth, day);
    return this.availableRanges.some(
      (range) =>
        currentDate >= range.startDate && currentDate <= range.endDate,
    );
  }

  protected isStart(day: number): boolean {
    if (!this.rangeStart) return false;

    return (
      day === this.rangeStart.getDate() &&
      this.selectedMonth === this.rangeStart.getMonth() &&
      this.selectedYear === this.rangeStart.getFullYear()
    );
  }

  protected isEnd(day: number): boolean {
    if (!this.rangeEnd) return false;

    return (
      day === this.rangeEnd.getDate() &&
      this.selectedMonth === this.rangeEnd.getMonth() &&
      this.selectedYear === this.rangeEnd.getFullYear()
    );
  }

  protected isStartOrEndDay(day: number): boolean {
    return this.isStart(day) || this.isEnd(day);
  }

  protected get userLanguage() {
    return this.lang || navigator.language;
  }

  protected get localizedWeekdays() {
    const isGerman = this.userLanguage.startsWith('de');
    return isGerman
      ? ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  }

  protected get localizedMonths() {
    const isGerman = this.userLanguage.startsWith('de');
    return isGerman
      ? [
          'Januar',
          'Februar',
          'März',
          'April',
          'Mai',
          'Juni',
          'Juli',
          'August',
          'September',
          'Oktober',
          'November',
          'Dezember',
        ]
      : [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ];
  }

  protected dateSelected(day: number) {
    const selectedDate = new Date(
      this.selectedYear,
      this.selectedMonth,
      day,
      this.selectedHours,
      this.selectedMinutes,
      this.selectedSeconds,
    );

    if (!this.rangeStart || this.rangeEnd) {
      this.rangeStart = selectedDate;
      this.rangeEnd = null;
    } else if (selectedDate < this.rangeStart) {
      this.rangeEnd = this.rangeStart;
      this.rangeStart = selectedDate;
    } else {
      this.rangeEnd = selectedDate;
    }

    this.dateRangeChanged.emit({
      startDate: this.rangeStart,
      endDate: this.rangeEnd,
    });
  }

  protected changeView(view: 'days' | 'months' | 'years') {
    this.view = view;
  }

  protected previousYears() {
    this.displayedYears = this.displayedYears.map((year) => year - 12);
  }

  protected nextYears() {
    this.displayedYears = this.displayedYears.map((year) => year + 12);
  }

  protected previousMonth() {
    this.selectedMonth--;
    if (this.selectedMonth < 0) {
      this.selectedMonth = 11;
      this.selectedYear--;
    }
    this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
  }

  protected nextMonth() {
    this.selectedMonth++;
    if (this.selectedMonth > 11) {
      this.selectedMonth = 0;
      this.selectedYear++;
    }
    this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
  }

  protected updateDisplayedDates(year: number, month: number) {
    const daysInWeek = 7;
    const weeksInGrid = 5;
    const totalDays = daysInWeek * weeksInGrid;

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDayOfMonth.getDay() || 7; // Adjusting for Monday as first day
    const daysInMonth = lastDayOfMonth.getDate();

    this.previousMonthsDates = [];
    this.dates = [];
    this.nextMonthsDates = [];

    // Fill previous month days
    for (let i = firstDayOfWeek - 1; i > 0; i--) {
      const day = new Date(year, month, 1 - i).getDate();
      this.previousMonthsDates.push(day);
    }

    // Fill current month days
    for (let i = 1; i <= daysInMonth; i++) {
      this.dates.push(i);
    }

    // Fill next month days
    let nextMonthDay = 1;
    while (
      this.dates.length +
        this.previousMonthsDates.length +
        this.nextMonthsDates.length <
      totalDays
    ) {
      this.nextMonthsDates.push(nextMonthDay++);
    }
  }

  protected get containerStyles() {
    return componentStylesByTheme(
      this.theme,
      calendarContainerSAG,
      calendarContainerSHS,
    )();
  }

  protected get pickerButtonStyles() {
    return componentStylesByTheme(
      this.theme,
      calendarButtonSAG,
      calendarButtonSHS,
    )();
  }

  protected get topTextStyles() {
    return componentStylesByTheme(
      this.theme,
      calendarTopTextSAG,
      calendarTopTextSHS,
    )();
  }

  protected get dayTextStyles() {
    return componentStylesByTheme(
      this.theme,
      calendarDayTextSAG,
      calendarDayTextSHS,
    )();
  }

  protected get dayBoxDisabledStyles() {
    return componentStylesByTheme(
      this.theme,
      calendarDisabledDayBoxSAG,
      calendarDisabledDayBoxSHS,
    )();
  }

  protected get dayBoxStyles() {
    return componentStylesByTheme(
      this.theme,
      calendarDayBoxSAG,
      calendarDayBoxSHS,
    )();
  }

  protected get monthBoxStyles() {
    return componentStylesByTheme(
      this.theme,
      calendarMonthBoxSAG,
      calendarMonthBoxSHS,
    )();
  }

  protected get dividerStyles() {
    return componentStylesByTheme(
      this.theme,
      calendarDividerSAG,
      calendarDividerSHS,
    )();
  }
}
